import { useQuery } from "@tanstack/react-query";
import { isPast, isToday } from "date-fns";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import Calendar from "@/CAREUI/interactive/Calendar";

import query from "@/Utils/request/query";
import { dateQueryString } from "@/Utils/utils";
import {
  summarizeSlotAvailability,
  useAvailabilityHeatmap,
} from "@/pages/Appointments/utils";
import {
  Appointment,
  GetSlotsForDayResponse,
  SchedulableResourceType,
} from "@/types/scheduling/schedule";
import scheduleApis from "@/types/scheduling/scheduleApi";
import { useState } from "react";

import AvailabilityDateButton from "./AvailabilityDateButton";

interface AppointmentDateSelectionProps {
  facilityId: string;
  resourceId?: string;
  resourceType: SchedulableResourceType;
  currentAppointment?: Appointment;
  setSelectedDate: (selectedDate: Date) => void;
  selectedDate: Date;
  disabled?: boolean;
}

export const AppointmentDateSelection = ({
  facilityId,
  resourceId,
  resourceType,
  currentAppointment,
  setSelectedDate,
  selectedDate,
  disabled = false,
}: AppointmentDateSelectionProps) => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(
    () => selectedDate ?? new Date(),
  );

  return (
    <div className="flex flex-col gap-3 md:min-w-121">
      {!resourceId ? (
        <span className="text-gray-950 font-medium dark:text-gray-50">
          {t("choose_resource")}
        </span>
      ) : (
        <h4 className="sm:hidden">{t("select_date")}</h4>
      )}
      <Calendar
        month={selectedMonth}
        onMonthChange={setSelectedMonth}
        setSelectedDate={setSelectedDate}
        renderDay={(date) => {
          return (
            <DateColumn
              date={date}
              facilityId={facilityId}
              resourceId={resourceId}
              resourceType={resourceType}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentAppointment={currentAppointment}
              selectedMonth={selectedMonth}
              disabled={disabled}
            />
          );
        }}
        highlightToday={false}
        className={cn(
          (!resourceId || disabled) && "opacity-50 pointer-events-none",
        )}
      />
    </div>
  );
};

interface DateColumnProps {
  facilityId: string;
  resourceId?: string;
  selectedDate: Date;
  resourceType: SchedulableResourceType;
  setSelectedDate: (date: Date) => void;
  date: Date;
  currentAppointment?: Appointment;
  selectedMonth: Date;
  disabled?: boolean;
}

const DateColumn = ({
  date,
  facilityId,
  resourceId,
  selectedDate,
  setSelectedDate,
  currentAppointment,
  selectedMonth,
  resourceType,
  disabled = false,
}: DateColumnProps) => {
  const heatmapQuery = useAvailabilityHeatmap({
    facilityId,
    resourceId,
    month: selectedMonth,
    resourceType,
  });

  const slotsTodayQuery = useQuery({
    queryKey: ["slots", facilityId, resourceId, dateQueryString(date)],
    queryFn: query(scheduleApis.slots.getSlotsForDay, {
      pathParams: { facilityId },
      body: {
        resource_type: resourceType,
        resource_id: resourceId || "",
        day: dateQueryString(date),
      },
    }),
    enabled: !!resourceId && isToday(date),
    select: (data: GetSlotsForDayResponse) => {
      if (currentAppointment) {
        return data.results.filter(
          (slot) => slot.id !== currentAppointment.token_slot.id,
        );
      }
      return data.results;
    },
  });

  const availability = (() => {
    // If the date is today and there are slots for today, ignore the heatmap
    // as the heatmap does not account for past slots and instead compute
    // the availability for the day based on the slots that are currently
    // available
    if (isToday(date) && slotsTodayQuery.data) {
      const slots = slotsTodayQuery.data.filter(
        (slot) => !isPast(slot.end_datetime),
      );
      return summarizeSlotAvailability(slots);
    }

    return heatmapQuery.data?.[dateQueryString(date)];
  })();

  return (
    <AvailabilityDateButton
      availability={availability}
      date={date}
      disabled={disabled}
      isLoading={heatmapQuery.isFetching && !availability}
      onSelectDate={setSelectedDate}
      selectedDate={selectedDate}
    />
  );
};
